import { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Model } from "survey-core";
import "@/features/tutor/utils/surveyCustomProperties";
import {
  choiceImage,
  choiceLabel,
  paintChoice,
} from "@/features/tutor/utils/surveyChoiceImage";
import { Survey } from "survey-react-ui";
import "survey-core/survey-core.min.css";
import "@/features/tutor/components/ExitTicketSurvey.css";
import api from "../../../../utils/axios-utils";
import { loadDemoAssessmentAttempt } from "../../service/demoAnalytics";
import { renderMath } from "@/utils/mathjax";
import {
  parseLatex,
  serializeToDisplay,
  ImageLightbox,
  makeImagesZoomable,
} from "@ever-tutor/et-ui";

/**
 * Question types the backend grades and counts towards total_questions
 * (mirrors GRADEABLE_QUESTION_TYPES in exit_ticket_scoring.py). Everything else
 * in a survey — image, html, panel — is supporting material attached to the
 * question next to it, so it carries no number of its own and the numbering
 * here matches the "13 / 20 correct" the rest of the UI reports.
 */
const GRADEABLE_QUESTION_TYPES = new Set([
  "text",
  "radiogroup",
  "checkbox",
  "dropdown",
  "comment",
]);

/**
 * The three outcomes a graded question can have, as one palette. The header
 * figures, the card border and the badge under the answer all read from it, so
 * a question is colour-coded consistently and the three can never drift apart.
 */
const RESULT_STATES = {
  correct: {
    label: "Correct Answer",
    color: "#558E01",
    background: "#F6FFE8",
    border: "#1C9C6D",
  },
  wrong: {
    label: "Wrong Answer",
    color: "#FF6C36",
    background: "#FFF0E8",
    border: "#FF6C36",
  },
  unanswered: {
    label: "Not Answered",
    color: "#777777",
    background: "#F4F4F4",
    border: "#B4B4B4",
  },
} as const;

/** Border for blocks that carry no result of their own — images, html. */
const SUPPORTING_BORDER = "#d1d5db";

const renderLatexToElement = (element: HTMLElement, latex: string) => {
  const doc = parseLatex(latex);
  const displayLatex = serializeToDisplay(doc);
  renderMath(element, displayLatex, { displayFractions: true });
};

type QuestionResult = {
  name: string;
  is_correct: boolean;
  student_answer?: string | null;
};

type AttemptDetail = {
  attempt_id: number;
  attempt_number: number;
  score: number | null;
  status: string;
  total_questions: number;
  correct_count: number;
  incorrect_count: number;
  not_attempted_count: number;
  time_spent_ms: number;
  lesson_name: string | null;
  student_name: string | null;
  survey_json: Record<string, unknown> | null;
  response_json: Record<string, unknown> | null;
  questions: QuestionResult[];
};

type ExitTicketDetailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  attemptId: number | null;
  studentName: string;
  assessmentLabel?: string;
  /**
   * Read the attempt from the demo capture instead of the API. The opener
   * decides, because it knows where the id came from: Broken Bow's Sessions tab
   * is live while its Standards tab is captured, so the same account can hand
   * this modal an id from either source.
   */
  isDemo: boolean;
};

const formatTime = (ms: number): string => {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const StatItem = ({
  label,
  value,
  color = "#252525",
}: {
  label: string;
  value: string | number;
  /** Ties the figure to the per-question colour language; omit for neutral. */
  color?: string;
}) => (
  <div className="text-center">
    <div className="font-mulish text-[15px] font-normal text-[#252525] mb-1">
      {label}
    </div>
    <div className="font-mulish text-[22px] font-bold" style={{ color }}>
      {value}
    </div>
  </div>
);

const SurveyDisplay = ({
  surveyJson,
  responseJson,
  questions,
}: {
  surveyJson: Record<string, unknown>;
  responseJson: Record<string, unknown>;
  questions: QuestionResult[];
}) => {
  const [lightboxImage, setLightboxImage] = useState<{
    src: string;
    alt: string;
  } | null>(null);
  const surveyWrapperRef = useRef<HTMLDivElement>(null);
  // Held in a ref so the model useMemo need not depend on the setter identity.
  const setLightboxImageRef = useRef(setLightboxImage);
  setLightboxImageRef.current = setLightboxImage;

  const questionMap = useMemo(
    () => Object.fromEntries(questions.map((q) => [q.name, q])),
    [questions],
  );

  const model = useMemo(() => {
    const m = new Model(surveyJson);
    m.mode = "display";
    m.showNavigationButtons = false;
    m.showCompletedPage = false;
    m.showProgressBar = "off";
    // Show all pages at once instead of defaulting to just page 1.
    m.questionsOnPageMode = "singlePage";
    // Numbering is rendered per question below, outside the title element.
    m.showQuestionNumbers = "off";
    m.data = responseJson;

    // Question.visibleIndex is only assigned while SurveyJS renders its own
    // numbers; with numbering off it stays -1 for every question. Take the
    // order from the model instead so the label survives that setting.
    const questionNumbers = new Map(
      m
        .getAllQuestions(true)
        .filter((q) => GRADEABLE_QUESTION_TYPES.has(q.getType()))
        .map((q, index) => [q.name, index + 1]),
    );

    m.onAfterRenderQuestion.add((_sender, options) => {
      const el = options.htmlElement;
      const question = options.question;
      const name = question.name;
      const qResult = questionMap[name];
      const isGradeable = GRADEABLE_QUESTION_TYPES.has(question.getType());
      // The backend scores an unanswered question as is_correct=false (it also
      // counts it under not_attempted_count), so a blank question would read as
      // "Wrong Answer". Treat a missing answer as its own state instead.
      //
      // A gradeable question missing from questionMap counts as unanswered too:
      // `questions` is built by walking response_json only, so older rows never
      // wrote an entry for questions the student skipped. The header already
      // tallies those under Not Attempted (exit_ticket.py:738-746) — without
      // this they would render like an image, with no badge at all.
      const isAnswered =
        qResult?.student_answer !== null &&
        qResult?.student_answer !== undefined &&
        qResult?.student_answer !== "";
      const isCorrect = qResult && isAnswered ? qResult.is_correct : undefined;
      const isUnanswered = isGradeable && !isAnswered;
      // null for supporting material (image, html) — it has no result at all.
      const resultState = isUnanswered
        ? RESULT_STATES.unanswered
        : isCorrect === undefined
          ? null
          : isCorrect
            ? RESULT_STATES.correct
            : RESULT_STATES.wrong;

      el.style.borderRadius = "10px";
      el.style.border = "1px solid";
      el.style.padding = "20px 20px 30px 20px";
      el.style.marginBottom = "20px";
      el.style.background = "#FFFFFF";
      el.style.borderColor = resultState?.border ?? SUPPORTING_BORDER;
      // The green glow reads as "there is a result here". An unanswered
      // question and supporting material both stay flat.
      el.style.boxShadow =
        isCorrect === undefined ? "none" : "0 0 10px rgba(28, 156, 109, 0.2)";

      // Question images + answer-option graphics become click-to-enlarge.
      makeImagesZoomable(el, (src, alt) =>
        setLightboxImageRef.current({ src, alt }),
      );

      // The number heads the whole SurveyJS row, not the card: an image and the
      // question it illustrates share one row (the survey JSON marks the
      // question startWithNewLine:false), so a heading above the row covers
      // both and shows they belong together.
      //
      // Own numbering rather than SurveyJS's: its number lives inside
      // .sd-question__title, which the LaTeX branch below hides in favour of a
      // rendered overlay, so math questions would silently lose it.
      const questionNumber = questionNumbers.get(name);
      const row = el.closest(".sd-row") as HTMLElement | null;
      if (questionNumber !== undefined && row?.parentElement) {
        const previous = row.previousElementSibling;
        const existingHeading =
          previous instanceof HTMLElement &&
          previous.classList.contains("question-number")
            ? previous
            : null;
        // A row can hold two gradeable questions — a question with
        // startWithNewLine:false following another question rather than an
        // image; production has three such rows. The heading names the first
        // of them, so the lowest number claimed for a row wins. Keeping the
        // minimum (rather than "first writer wins") also stays correct when
        // SurveyJS re-renders and replays the questions in any order.
        const claimed = Number(existingHeading?.dataset.questionNumber);
        const isAlreadyClaimedByEarlier =
          !Number.isNaN(claimed) && claimed < questionNumber;

        const heading =
          existingHeading ??
          row.parentElement.insertBefore(document.createElement("div"), row);
        heading.className = "question-number";
        heading.style.cssText = `
          font-family: "Mulish", sans-serif;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #B4B4B4;
          margin-bottom: 12px;
          margin-top: 12px;
        `;
        if (!isAlreadyClaimedByEarlier) {
          heading.dataset.questionNumber = String(questionNumber);
          heading.textContent = `Question ${questionNumber}`;
        }
      }

      const existing = el.querySelector(".result-badge");
      if (existing) existing.remove();

      if (resultState) {
        const badge = document.createElement("span");
        badge.className = "result-badge";
        badge.style.cssText = `
          display: inline-block;
          font-family: "Mulish", sans-serif;
          font-size: 12px;
          font-weight: 700;
          color: ${resultState.color};
          background-color: ${resultState.background};
          border-radius: 10px;
          padding: 10px;
          margin-top: 8px;
        `;
        badge.textContent = resultState.label;
        el.appendChild(badge);
      }

      const existingMeta = el.querySelector(".answer-meta");
      if (existingMeta) existingMeta.remove();

      requestAnimationFrame(() => {
        const rawTitle = question.title || question.name;
        if (rawTitle.includes("\\") || rawTitle.includes("$")) {
          const titleEl = el.querySelector(
            ".sd-question__title",
          ) as HTMLElement | null;
          // Guard via previousElementSibling (not dataset) because this callback may fire
          // multiple times on re-render — SurveyJS recreates the DOM without our dataset flag.
          if (
            titleEl &&
            !titleEl.previousElementSibling?.classList.contains(
              "et-katex-display--title",
            )
          ) {
            const overlay = document.createElement("div");
            overlay.className = "et-katex-display--title";
            titleEl.style.display = "none";
            titleEl.parentElement?.insertBefore(overlay, titleEl);
            renderLatexToElement(overlay, rawTitle);
          }
        }

        const q = question as {
          visibleChoices?: unknown[];
          choices?: unknown[];
        };
        const choices = q.visibleChoices || q.choices || [];
        let painted = false;
        el.querySelectorAll(".sd-item__control-label").forEach(
          (labelEl, idx) => {
            const htmlLabel = labelEl as HTMLElement;
            if (
              htmlLabel.previousElementSibling?.classList.contains(
                "et-katex-display--radio-option",
              )
            ) {
              return;
            }
            const choice = choices[idx];
            const image = choiceImage(choice, idx);
            const text =
              choice != null
                ? choiceLabel(choice)
                : htmlLabel.textContent || "";
            if (!image && !text.includes("\\") && !text.includes("$")) return;
            const overlay = document.createElement("div");
            overlay.className = "et-katex-display--radio-option";
            htmlLabel.style.display = "none";
            htmlLabel.parentElement?.insertBefore(overlay, htmlLabel);
            paintChoice(overlay, image, text, renderLatexToElement);
            painted = painted || Boolean(image);
          },
        );
        if (painted) {
          makeImagesZoomable(el, (src, alt) => setLightboxImage({ src, alt }));
        }

        const inputEl = el.querySelector(
          "input.sd-input, textarea.sd-input",
        ) as HTMLInputElement | null;
        if (
          inputEl &&
          !inputEl.nextElementSibling?.classList.contains("katex-display")
        ) {
          const val = inputEl.value || "";
          if (
            val &&
            (val.includes("\\") || val.includes("frac") || val.includes("$"))
          ) {
            const display = document.createElement("div");
            display.className = "katex-display";
            display.style.cssText =
              "padding: 8px 12px; background: #f9fafb; border-radius: 8px; min-height: 36px; display: flex; align-items: center;";
            renderLatexToElement(display, val);
            inputEl.style.display = "none";
            inputEl.parentNode?.insertBefore(display, inputEl.nextSibling);
          }
        }

        if (
          qResult &&
          (question.getType() === "radiogroup" ||
            question.getType() === "checkbox")
        ) {
          const studentAns = qResult.student_answer;
          const correctAns = question.correctAnswer as string | undefined;
          el.querySelectorAll(".sd-item").forEach((item: Element) => {
            const itemEl = item as HTMLElement;
            itemEl.style.cssText =
              "background: transparent !important; display: flex; align-items: center; gap: 10px; padding: 8px 0; border: none !important; box-shadow: none !important;";
            const label = item.querySelector(".sd-item__control-label");
            const decorator = item.querySelector(
              ".sd-radio__decorator, .sd-checkbox__decorator",
            ) as HTMLElement | null;
            const value =
              (item.querySelector("input") as HTMLInputElement | null)?.value ??
              label?.textContent?.trim() ??
              "";
            const isStudentChoice = value === studentAns;
            const isCorrectChoice = value === correctAns;
            const existingBadge = item.querySelector(".choice-badge");
            if (existingBadge) existingBadge.remove();

            if (decorator) {
              decorator.style.cssText = `
                width: 24px; height: 24px; min-width: 24px; flex-shrink: 0;
                border: 2px solid #d1d5db; border-radius: 50%;
                background: #f3f4f6; display: flex;
                align-items: center; justify-content: center;
                position: relative;
              `;
              decorator
                .querySelectorAll("svg, .sd-radio__svg")
                .forEach((s) => ((s as HTMLElement).style.display = "none"));
              const existingDot = decorator.querySelector(".radio-dot");
              if (existingDot) existingDot.remove();

              if (isCorrectChoice || isStudentChoice) {
                const dot = document.createElement("span");
                dot.className = "radio-dot";
                dot.style.cssText = `
                  width: 14px; height: 14px; border-radius: 50%;
                  background: ${isCorrectChoice ? "#7AB32B" : "#FF6C36"};
                  position: absolute;
                  top: 50%; left: 50%;
                  transform: translate(-50%, -50%);
                `;
                decorator.appendChild(dot);
              }
            }

            if (isCorrectChoice || isStudentChoice) {
              // Badge goes into the flex row that wraps decorator + label (overlay's parent).
              // Do not use the overlay itself as a flex container — it is a KaTeX
              // container and turning it into flex breaks KaTeX's inline layout.
              const overlay = item.querySelector(
                ".et-katex-display--radio-option",
              ) as HTMLElement | null;
              const badgeContainer = (overlay?.parentElement ??
                label?.parentElement) as HTMLElement | null;
              if (badgeContainer) {
                badgeContainer.style.display = "flex";
                badgeContainer.style.alignItems = "center";
                badgeContainer.style.gap = "8px";
              }
              const badge = document.createElement("span");
              badge.className = "choice-badge";
              badge.style.cssText = `
                flex-shrink: 0;
                font-family: "Mulish", sans-serif;
                font-size: 11px;
                font-weight: 600;
                border-radius: 6px;
                padding: 4px 8px;
                white-space: nowrap;
                color: ${isCorrectChoice ? "#558E01" : "#FF6C36"};
                background: ${isCorrectChoice ? "#F6FFE8" : "#FFF0E8"};
              `;
              badge.textContent = isCorrectChoice
                ? "Correct Answer"
                : "Students Answer";
              badgeContainer?.appendChild(badge);
            }
          });
        }
      });
    });

    return m;
  }, [surveyJson, responseJson, questionMap]);

  return (
    <div className="detail-survey-wrapper" ref={surveyWrapperRef}>
      <Survey model={model} />
      <ImageLightbox
        isOpen={!!lightboxImage}
        src={lightboxImage?.src ?? null}
        alt={lightboxImage?.alt ?? ""}
        onClose={() => {
          setLightboxImage(null);
          // The lightbox does not restore focus; without this it lands on
          // <body>, outside the dialog that handles Escape and Tab.
          surveyWrapperRef.current
            ?.closest<HTMLElement>('[role="dialog"]')
            ?.focus();
        }}
      />
    </div>
  );
};

const ExitTicketDetailModal = ({
  isOpen,
  onClose,
  attemptId,
  studentName,
  assessmentLabel = "Exit Ticket",
  isDemo,
}: ExitTicketDetailModalProps) => {
  const [data, setData] = useState<AttemptDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const backdropRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    const background = Array.from(document.body.children).filter(
      (el): el is HTMLElement =>
        el instanceof HTMLElement && el !== backdropRef.current,
    );
    const previousInert = background.map((el) => [el, el.inert] as const);
    background.forEach((el) => {
      el.inert = true;
    });
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previousInert.forEach(([el, inert]) => {
        el.inert = inert;
      });
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !attemptId) return;
    let cancelled = false;
    setData(null);
    setError(null);
    setIsLoading(true);
    const request = isDemo
      ? loadDemoAssessmentAttempt(attemptId)
      : api
          .get(`/exit-ticket/attempts/${attemptId}/details`)
          .then((r) => r.data);
    request
      .then((payload) => {
        if (!cancelled) setData(payload as AttemptDetail | null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch attempt details:", err);
        setError("Results could not be loaded. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, attemptId, isDemo, retry]);

  if (!isOpen) return null;

  const firstName = studentName.split(" ")[0];

  return createPortal(
    <div
      ref={backdropRef}
      className="et-assessment-modal et-product-surface fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />

      <div
        ref={panelRef}
        className="et-assessment-modal-panel relative w-[82.71%] h-[90vh]"
        role="dialog"
        aria-modal="true"
        aria-label={`${studentName}: ${assessmentLabel} results`}
        tabIndex={-1}
        onKeyDown={(event) => {
          // A nested image lightbox owns its own keys, even when it is portalled.
          if (!panelRef.current?.contains(event.target as Node)) return;
          if (event.key === "Escape") {
            event.stopPropagation();
            onClose();
          }
          if (event.key !== "Tab") return;
          const elements = Array.from(
            panelRef.current.querySelectorAll<HTMLElement>(
              'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]',
            ),
          ).filter((el) => el.getClientRects().length > 0);
          const first = elements[0],
            last = elements[elements.length - 1];
          if (!first) {
            event.preventDefault();
            return;
          }
          if (
            event.shiftKey &&
            (document.activeElement === first ||
              document.activeElement === panelRef.current)
          ) {
            event.preventDefault();
            last.focus();
          }
          if (
            !event.shiftKey &&
            (document.activeElement === last ||
              document.activeElement === panelRef.current)
          ) {
            event.preventDefault();
            first.focus();
          }
        }}
      >
        <button
          onClick={onClose}
          className="absolute -top-8 right-6 z-20 flex items-center gap-1 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-lg transition hover:bg-gray-50"
        >
          <X size={14} />
          Close
        </button>

        <div className="relative h-full flex flex-col rounded-[20px] bg-white shadow-[0_20px_60px_rgba(15,65,45,0.25)] overflow-hidden">
          <div className="relative flex-shrink-0 bg-white px-8 pt-8 pb-4 border-b border-gray-100">
            <div>
              <p className="font-mulish text-[15px] font-semibold text-[#252525] mb-1">
                {assessmentLabel} of {firstName.toUpperCase()}
              </p>
              <div className="flex items-end justify-between gap-6">
                <h2 className="font-mulish text-[25px] font-bold text-[#007047]">
                  {data?.lesson_name ??
                    (isLoading ? "Loading..." : "Assessment results")}
                </h2>
                {data && (
                  <div className="flex items-center gap-8 text-sm">
                    <StatItem label="Total" value={data.total_questions} />
                    <StatItem
                      label="Correct"
                      value={data.correct_count}
                      color={RESULT_STATES.correct.color}
                    />
                    <StatItem
                      label="Incorrect"
                      value={data.incorrect_count}
                      color={RESULT_STATES.wrong.color}
                    />
                    <StatItem
                      label="Not Attempted"
                      value={data.not_attempted_count}
                      color={RESULT_STATES.unanswered.color}
                    />
                    <StatItem
                      label="Time Spent"
                      value={formatTime(data.time_spent_ms)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-white px-8 pb-8">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="relative w-16 h-16 mb-4">
                  <div className="absolute inset-0 rounded-full border-4 border-[#e3eae1]" />
                  <div className="absolute inset-0 rounded-full border-4 border-[#138c61] border-t-transparent animate-spin" />
                </div>
                <div className="text-[#1f3b2d] font-medium">
                  Loading results...
                </div>
              </div>
            ) : error ? (
              <div role="alert" className="et-assessment-error">
                <p>{error}</p>
                <button type="button" onClick={() => setRetry((n) => n + 1)}>
                  Try again
                </button>
              </div>
            ) : data?.survey_json && data?.response_json ? (
              <div className="mt-6">
                <SurveyDisplay
                  surveyJson={data.survey_json}
                  responseJson={data.response_json}
                  questions={data.questions}
                />
              </div>
            ) : (
              <p role="status" className="py-8">
                No detailed response is available for this attempt.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ExitTicketDetailModal;
